import { useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { convertsCentsToCurrency } from '@npi/buddy-utils';

import { useParams } from '@redwoodjs/router';
import { useQuery } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import ViewPDFModal from 'src/components/ViewPDFModal';
import {
    ACCOUNT_STATEMENT,
    GET_ACCOUNT_STATEMENT_PDF,
} from 'src/graphql/queries/accountStatement.query';
import { useCurrency } from 'src/hooks/useCurrency';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { stringToCamelCase } from 'src/lib/formatters';
import { formatDate, onError } from 'src/utils/utils';

import CallToActions from './components/CallToActions';
import StatementBillingDetails from './StatementBillingDetails';
import StatementDetails from './StatementDetails';
import StatementItemTable from './StatementItemTable';
import StatementTotals from './StatementTotals';
import TableKey from './TableKey';
import { AccountStatementItemType } from './types';

function ViewAccountStatementPage() {
    const { id } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [getAccountStatementPDF, { data: statementPDF, loading: isLoadingPDF }] = useLazyQuery(
        GET_ACCOUNT_STATEMENT_PDF,
        {
            fetchPolicy: 'cache-and-network',
            onError,
        }
    );

    const { data, loading } = useQuery(ACCOUNT_STATEMENT, {
        variables: {
            statementId: parseInt(id, 10),
        },
        fetchPolicy: 'cache-and-network',
        onError,
    });

    const { currencySymbol } = useCurrency(
        data?.getAccountStatement?.merchant?.Partner?.countryDetails
    );

    const statementData = useMemo(() => {
        const getAccountStatement = data?.getAccountStatement ?? {};
        let balanceLabel = '';
        let lastItemId = 0;

        const accountStatementItems =
            getAccountStatement.accountStatementItems?.map((item) => {
                let credit = '';
                let debit = '';

                if (item.itemType === AccountStatementItemType.BALANCE) {
                    balanceLabel = stringToCamelCase(item.description);
                }
                if (item.credit) {
                    credit = `${currencySymbol}${convertsCentsToCurrency(item.credit)}`;
                } else if (item.debit) {
                    debit = `${currencySymbol}${convertsCentsToCurrency(item.debit)}`;
                }

                lastItemId = item.id > lastItemId ? item.id : lastItemId;

                return {
                    ...item,
                    transactionDate: formatDate({ date: new Date(item.transactionDate) }),
                    credit,
                    debit,
                    rollingBalance: `${currencySymbol}${convertsCentsToCurrency(
                        item.rollingBalance
                    )}`,
                };
            }) ?? [];

        const {
            outstandingBalance,
            balanceBroughtForward,
            totalInvoiceAmount,
            totalCreditApplied,
            totalPaymentsMade,
            accountStatementId,
            startDateTime,
            endDateTime,
            statementDate,
        } = getAccountStatement ?? {};

        const totals = {
            outstandingBalance: `${currencySymbol}${convertsCentsToCurrency(outstandingBalance)}`,
            balanceBroughtForward: `${currencySymbol}${convertsCentsToCurrency(
                balanceBroughtForward
            )}`,
            totalInvoiceAmount: `${currencySymbol}${convertsCentsToCurrency(totalInvoiceAmount)}`,
            totalCreditApplied: `${currencySymbol}${convertsCentsToCurrency(totalCreditApplied)}`,
            totalPaymentsMade: `${currencySymbol}${convertsCentsToCurrency(totalPaymentsMade)}`,
            balanceLabel,
        };

        const statementDetails = {
            statementId: accountStatementId,
            startDate: formatDate({ date: new Date(startDateTime) }),
            endDate: formatDate({ date: new Date(endDateTime) }),
            statementDate: formatDate({ date: new Date(statementDate) }),
        };

        return { ...getAccountStatement, totals, accountStatementItems, statementDetails };
    }, [data]);

    const emptyPdfBlob = new Blob([], { type: 'application/pdf' });
    const file = new File(
        [emptyPdfBlob],
        `account-statement-${data?.getAccountStatement?.accountStatementId}.pdf`,
        {
            type: 'application/pdf',
        }
    );

    const handleOpenModal = async () => {
        setIsModalOpen(true);
        await getAccountStatementPDF({
            variables: {
                statementId: parseInt(id),
            },
        });
    };

    return (
        <>
            <PageContent
                heading="View Account Statement"
                callToActions={<CallToActions onView={handleOpenModal} />}
            >
                <Flex flexDirection="column" gap="24px">
                    <Flex gap="inherit">
                        <Flex flexDirection="column" flex={1} gap="24px">
                            <StatementDetails {...statementData.statementDetails} />
                        </Flex>
                        <Flex flexDirection="column" flex={2} gap="24px">
                            <StatementBillingDetails {...statementData.merchant} />
                        </Flex>
                    </Flex>
                    <Flex flexDirection="column" gap="24px">
                        <StatementTotals {...statementData.totals} />
                    </Flex>
                    <Flex flexDirection="column" gap="24px">
                        <Flex flexDirection="column" flex={1} gap="24px">
                            <TableKey />
                        </Flex>
                        <Flex>
                            <StatementItemTable
                                loading={loading}
                                tableData={statementData.accountStatementItems}
                            />
                        </Flex>
                    </Flex>
                </Flex>
            </PageContent>

            {isLoadingPDF ? (
                <Loader />
            ) : (
                <ViewPDFModal
                    title="View Account Statement PDF"
                    file={file}
                    fileURL={statementPDF?.getAccountStatementPDF?.downloadLink}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={() => {
                        return setIsModalOpen(false);
                    }}
                    maxWidth="lg"
                    dialogStylesSX={{ padding: '8px' }}
                />
            )}
        </>
    );
}

export default ViewAccountStatementPage;
